import React, { useEffect, useState } from "react"
import type { StepConfig, VariableGroups, VariableHint } from "./types"
import { INPUT_CLASS } from "./constants"
import { VariableBrowser } from "./VariableBrowser"

interface HttpRequestConfigProps {
  config: StepConfig
  disabled: boolean
  onChange: (updates: Partial<StepConfig>) => void
  variableHints?: VariableGroups
}

const methods = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"] as const

function formatHeaders(headers: Record<string, string> | undefined) {
  return JSON.stringify(headers || {}, null, 2)
}

export function HttpRequestConfig({ config, disabled, onChange, variableHints }: HttpRequestConfigProps) {
  const [headersText, setHeadersText] = useState(() => formatHeaders(config.headers))
  const [headersError, setHeadersError] = useState<string | null>(null)
  const [templateTarget, setTemplateTarget] = useState<"url_template" | "body_template">("url_template")
  useEffect(() => setHeadersText(formatHeaders(config.headers)), [config.headers])

  const insertVariable = (hint: VariableHint) => {
    const token = hint.template || `{{ ${hint.path.replace(/^\//, "").replaceAll("/", ".")} }}`
    onChange({ [templateTarget]: `${config[templateTarget] || ""}${token}` })
  }

  const bodyMode = config.body_mode || "none"
  const changeHeaders = (value: string) => {
    setHeadersText(value)
    try {
      const headers = JSON.parse(value)
      if (!headers || Array.isArray(headers) || typeof headers !== "object" || Object.values(headers).some((header) => typeof header !== "string")) {
        throw new Error("Headers must be a JSON object with string values")
      }
      onChange({ headers })
      setHeadersError(null)
    } catch (error) {
      setHeadersError(error instanceof Error ? error.message : "Headers must be valid JSON")
    }
  }

  return (
    <fieldset disabled={disabled} className="space-y-3 disabled:opacity-60">
      <VariableBrowser groups={variableHints} onInsert={insertVariable} />
      {variableHints && (
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Insert into
          <select
            aria-label="Variable insertion target"
            value={templateTarget}
            onChange={(event) => setTemplateTarget(event.target.value as typeof templateTarget)}
            className={`${INPUT_CLASS} max-w-xs`}
          >
            <option value="url_template">URL template</option>
            <option value="body_template">Body template</option>
          </select>
        </label>
      )}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-700">URL template</label>
        <input
          value={config.url_template || ""}
          onChange={(event) => onChange({ url_template: event.target.value })}
          className={INPUT_CLASS}
          placeholder="https://api.example.com/orders/{{ request.body.id }}"
        />
        <p className="text-xs text-gray-500">Templates have a restricted filter set. Requests never follow redirects or retry automatically.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Method</label>
          <select
            value={config.method || "POST"}
            onChange={(event) => {
              const method = event.target.value as StepConfig["method"]
              onChange({ method, ...(method === "GET" || method === "HEAD" ? { body_mode: "none", body_template: undefined } : {}) })
            }}
            className={INPUT_CLASS}
          >
            {methods.map((method) => <option key={method} value={method}>{method}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Body</label>
          <select
            value={bodyMode}
            onChange={(event) => {
              const nextMode = event.target.value as StepConfig["body_mode"]
              onChange(nextMode === "none" ? { body_mode: nextMode, body_template: undefined } : { body_mode: nextMode, body_template: config.body_template || "" })
            }}
            disabled={config.method === "GET" || config.method === "HEAD"}
            className={INPUT_CLASS}
          >
            <option value="none">No body</option>
            <option value="json">JSON template</option>
            <option value="text">Text template</option>
          </select>
        </div>
      </div>
      {bodyMode !== "none" && (
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Body template</label>
          <textarea
            value={config.body_template || ""}
            onChange={(event) => onChange({ body_template: event.target.value })}
            className={INPUT_CLASS}
            rows={4}
            spellCheck={false}
          />
        </div>
      )}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-700">Headers (JSON)</label>
        <textarea
          aria-label="HTTP request headers JSON"
          value={headersText}
          onChange={(event) => changeHeaders(event.target.value)}
          className={`${INPUT_CLASS} font-mono ${headersError ? "border-red-300" : ""}`}
          rows={4}
          spellCheck={false}
        />
        {headersError && <p className="text-xs text-red-600" role="alert">{headersError}</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Read timeout (seconds)</label>
          <input type="number" min={1} max={30} value={config.read_timeout_seconds ?? 30} onChange={(event) => onChange({ read_timeout_seconds: Number(event.target.value) || 1 })} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Response capture (bytes)</label>
          <input type="number" min={1} max={262144} value={config.response_capture_bytes ?? 4096} onChange={(event) => onChange({ response_capture_bytes: Number(event.target.value) || 1 })} className={INPUT_CLASS} />
        </div>
      </div>
    </fieldset>
  )
}
